import { useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { supabase } from '@/lib/supabase';
import type { MeuTalao } from '../types';
import CartelaVendedor from './CartelaVendedor';

interface Props {
  t: MeuTalao;
  link: string;
  talaoId: number | null;
  rifaId: number;
  vendedorId: number | null;
  abrirQRInicial?: boolean;
}

interface MensagemVenda {
  ok: boolean;
  texto?: string;
  link?: string;
  completa?: string;
  titulo?: string;
  valor?: string;
  restam?: number;
  premios?: number;
  vendendo?: boolean;
  erro?: string;
}

export default function BlocoQR({
  t,
  link,
  talaoId,
  rifaId,
  vendedorId,
  abrirQRInicial,
}: Props) {
  // Aberto por padrão no desktop (tem espaço) ou quando pedido; colapsado no celular.
  const [aberto, setAberto] = useState(
    () => abrirQRInicial || window.matchMedia('(min-width: 1024px)').matches,
  );
  const [copiado, setCopiado] = useState(false);
  const [preparando, setPreparando] = useState(false);
  const [erroMensagem, setErroMensagem] = useState<string | null>(null);

  async function buscarMensagem(): Promise<string | null> {
    const token = t.token;
    if (!token) return null;
    const { data, error } = await supabase.rpc('mensagem_de_venda', { p_token: token });
    if (error) return null;
    const res = data as MensagemVenda | null;
    if (!res || res.ok !== true || !res.completa) return null;
    return res.completa;
  }

  async function compartilhar() {
    setErroMensagem(null);
    setPreparando(true);
    try {
      const completa = await buscarMensagem();
      if (!completa) {
        setErroMensagem('Não consegui montar a mensagem, tente de novo');
        return;
      }
      if (navigator.share) {
        await navigator.share({ text: completa });
      } else {
        await navigator.clipboard.writeText(completa);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2500);
      }
    } catch {
      /* usuário cancelou ou share indisponível: não bloqueia */
    } finally {
      setPreparando(false);
    }
  }

  async function copiarMensagem() {
    setErroMensagem(null);
    setPreparando(true);
    try {
      const completa = await buscarMensagem();
      if (!completa) {
        setErroMensagem('Não consegui montar a mensagem, tente de novo');
        return;
      }
      await navigator.clipboard.writeText(completa);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      /* não bloqueia */
    } finally {
      setPreparando(false);
    }
  }

  const semToken = !t.token;

  return (
    <div className="overflow-hidden rounded-3xl border border-secondary-100 bg-white">
      {/* Cabeçalho que abre/fecha */}
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-background-50"
        aria-expanded={aberto}
      >
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-qr-code-line text-xl" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground-950">
              Meu QR para vender pessoalmente
            </p>
            <p className="mt-0.5 text-xs text-secondary-500">Mostre para quem for comprar</p>
          </div>
        </div>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-secondary-500">
          <i className={aberto ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line text-lg'} />
        </span>
      </button>

      {aberto && (
        <div className="animate-subir border-t border-secondary-100 px-5 pb-5 pt-4">
          {semToken ? (
            <p className="py-4 text-center text-sm font-medium text-foreground-700">
              Você ainda não tem talão nesta ação entre amigos.
            </p>
          ) : (
            <>
              <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center sm:gap-6">
                <div className="rounded-2xl border border-secondary-200 bg-white p-3">
                  <QRCode value={link} size={220} />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-sm font-bold tracking-[0.25em] text-foreground-950">
                    {(t.token ?? '').toUpperCase()}
                  </p>
                  <p className="mt-2 max-w-[220px] text-sm text-secondary-600">
                    Quem escaneia escolhe quantos quer e paga no Pix.
                  </p>
                </div>
              </div>

              <CartelaVendedor rifaId={rifaId} vendedorId={vendedorId} />
            </>
          )}

          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {t.token && (
              <button
                type="button"
                onClick={compartilhar}
                disabled={preparando}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-4 w-4 items-center justify-center text-green-600">
                  <i className="ri-share-forward-line" />
                </span>
                {preparando ? 'Preparando...' : 'Compartilhar'}
              </button>
            )}

            {t.token && (
              <button
                type="button"
                onClick={copiarMensagem}
                disabled={preparando}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                </span>
                {copiado ? 'Copiado!' : 'Copiar mensagem'}
              </button>
            )}

            {talaoId != null ? (
              <Link
                to={`/talao/${talaoId}/imprimir`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-300 bg-white px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-50"
              >
                <span className="flex h-4 w-4 items-center justify-center text-secondary-600">
                  <i className="ri-printer-line" />
                </span>
                Imprimir
              </Link>
            ) : (
              <button
                type="button"
                disabled
                className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-2xl border border-secondary-200 bg-secondary-50 px-4 py-2.5 text-sm font-semibold text-secondary-400"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-printer-line" />
                </span>
                Imprimir
              </button>
            )}
          </div>

          {erroMensagem && (
            <p className="mt-3 text-center text-sm font-medium text-danger-600">{erroMensagem}</p>
          )}
        </div>
      )}
    </div>
  );
}